import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { PaginationState } from "@tanstack/react-table";
import type { VisitStatus } from "@alm/validation";
import { instantAtFacilityTime, PAGE_SIZES } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";

import { DataTableColumns } from "@/components/app/patterns/data-table-columns";
import { ListToolbar } from "@/components/app/patterns/list-toolbar";
import { SearchField } from "@/components/app/patterns/search-field";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { ClipboardList, UserPlus } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { optionalDay, optionalInt, optionalOneOf, searchPatch, optionalSearch } from "@/lib/search";
import { useZone } from "@/lib/facility/zone";
import { guardRoute } from "@/lib/policy/route-guard";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { BadgePreviewDialog } from "./-components/badge-preview-dialog";
import { CheckInDialog } from "./-components/check-in-dialog";
import { EarlierVisitsNotice } from "./-components/earlier-visits-notice";
import { EvacuationDialog } from "./-components/evacuation-dialog";
import type { VisitRow } from "./-components/visit-row";
import {
  dateRangeCount,
  type VisitDateRange,
  VisitorDateFilter,
} from "./-components/visitor-date-filter";
import { useVisitActions } from "./-components/use-visit-actions";
import { VisitorNav } from "./-components/visitor-nav";
import { VisitorStatusChips } from "./-components/visitor-status-chips";
import { VisitorTable } from "./-components/visitor-table";

const STATUS_PARAMS = ["checked_out", "all"] as const;

export const Route = createFileRoute("/_authenticated/_visitors/visitors/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    status?: (typeof STATUS_PARAMS)[number];
    q?: string;
    from?: string;
    to?: string;
    page?: number;
    size?: number;
  } => ({
    status: optionalOneOf(STATUS_PARAMS, search.status),
    q: optionalSearch(search.q),
    from: optionalDay(search.from),
    to: optionalDay(search.to),
    page: optionalInt(search.page, 2, 100000),
    size: PAGE_SIZES.find((size) => size === Number(search.size)),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "visitors"),
  component: VisitorsPage,
});

type Dialog =
  | { kind: "check-in" }
  | { kind: "preview"; visit: VisitRow }
  | { kind: "evacuation" }
  | null;

const EMPTY_COPY: Record<VisitStatus, { title: string; description: string }> = {
  on_site: {
    title: "Nobody is in the building right now",
    description: "Visitors appear here as they check in at the kiosk or the desk.",
  },
  checked_out: {
    title: "Nobody has checked out yet",
    description:
      "A visit moves here when the visitor signs out at the kiosk or the desk checks them out.",
  },
  all: {
    title: "Nobody has checked in yet",
    description:
      "Every visit to the building is logged here, from the kiosk at the door or from the desk.",
  },
};

/** The sign-in sheet: who is in the building now, who has been, and the badges that go with
 * them. */
function VisitorsPage() {
  const { facilityId } = useFacility();
  const canManage = useCan("visitor:manage");
  const { status: statusParam, q: needle, from, to, page = 1, size } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const refine = (next: Partial<ReturnType<typeof Route.useSearch>>) =>
    patch({ page: undefined, ...next });
  const status: VisitStatus = statusParam ?? "on_site";
  const { text: search, change: setSearch } = useSearchParamText(needle, (q) => refine({ q }));
  const range: VisitDateRange = { from: from ?? "", to: to ?? "" };
  const pagination: PaginationState = { pageIndex: page - 1, pageSize: size ?? PAGE_SIZES[0] };
  const setPagination = (next: PaginationState) =>
    next.pageSize !== pagination.pageSize
      ? refine({ size: next.pageSize === PAGE_SIZES[0] ? undefined : next.pageSize })
      : patch({ page: next.pageIndex > 0 ? next.pageIndex + 1 : undefined });
  const [dialog, setDialog] = useState<Dialog>(null);
  // A day on the filter is the building's day, so the bounds are drawn in its zone.
  const zone = useZone(facilityId === "all" ? null : facilityId);

  const list = useQuery({
    ...orpc.visitors.list.queryOptions({
      input: {
        facilityId,
        status,
        search: needle || undefined,
        from: range.from ? instantAtFacilityTime(range.from, "00:00", zone) : undefined,
        to: range.to
          ? new Date(instantAtFacilityTime(range.to, "23:59", zone).getTime() + 59_999)
          : undefined,
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );
  const { busyVisitId, checkOut, reprint } = useVisitActions();

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const hasFilters = Boolean(needle) || dateRangeCount(range) > 0;
  const facilityName =
    facilityId === "all"
      ? "All buildings"
      : (facilities.data?.find((facility) => facility.id === facilityId)?.name ?? "This building");
  const show = (next: VisitStatus) => refine({ status: optionalOneOf(STATUS_PARAMS, next) });
  const clear = () => refine({ q: undefined, from: undefined, to: undefined });
  const empty = EMPTY_COPY[status];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Visitors"
        count={list.isSuccess ? total : undefined}
        meta={
          list.isPending
            ? "Loading…"
            : list.isError
              ? undefined
              : status === "on_site"
                ? `${total} in the building${hasFilters ? " matching" : ""}`
                : `${total} ${total === 1 ? "visit" : "visits"}${hasFilters ? " matching" : ""}`
        }
        actions={
          <>
            <Button variant="outline" onClick={() => setDialog({ kind: "evacuation" })}>
              <ClipboardList data-icon="inline-start" aria-hidden="true" />
              Evacuation list
            </Button>
            {canManage ? (
              <Button onClick={() => setDialog({ kind: "check-in" })}>
                <UserPlus data-icon="inline-start" aria-hidden="true" />
                Check in visitor
              </Button>
            ) : null}
          </>
        }
      />
      <VisitorNav />
      {facilityId !== "all" && status === "on_site" ? (
        <EarlierVisitsNotice facilityId={facilityId} />
      ) : null}

      {list.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load the visitor log."
            error={list.error}
            onRetry={() => void list.refetch()}
            retrying={list.isFetching}
          />
        </Card>
      ) : (
        <VisitorTable
          rows={rows}
          rowCount={total}
          pagination={pagination}
          onPaginationChange={setPagination}
          pending={list.isPending}
          canManage={canManage}
          busyVisitId={busyVisitId}
          onCheckOut={(visit) => checkOut(visit.id)}
          onReprint={(visit) => reprint(visit.id)}
          onPreview={(visit) => setDialog({ kind: "preview", visit })}
          renderToolbar={(table) => (
            <ListToolbar
              search={
                <SearchField
                  value={search}
                  onChange={setSearch}
                  placeholder="Search visitor, company or resident"
                  label="Search visitors"
                />
              }
              chips={<VisitorStatusChips value={status} onChange={show} />}
              trailing={
                <>
                  <VisitorDateFilter
                    range={range}
                    onChange={(next) =>
                      refine({ from: next.from || undefined, to: next.to || undefined })
                    }
                  />
                  {hasFilters ? (
                    <Button variant="ghost" onClick={clear}>
                      Clear filters
                    </Button>
                  ) : null}
                  <DataTableColumns table={table} />
                </>
              }
            />
          )}
          empty={
            <EmptyState
              title={hasFilters ? "No visits match these filters" : empty.title}
              description={
                hasFilters ? "Widen the search or clear a filter to see more." : empty.description
              }
              action={
                hasFilters ? (
                  <Button variant="outline" onClick={clear}>
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          }
        />
      )}

      {dialog?.kind === "check-in" ? (
        <CheckInDialog
          facilityId={facilityId === "all" ? null : facilityId}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "preview" ? (
        <BadgePreviewDialog
          visit={dialog.visit}
          canManage={canManage}
          onReprint={(visit) => reprint(visit.id)}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "evacuation" ? (
        <EvacuationDialog
          facilityId={facilityId}
          facilityName={facilityName}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}
